import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PatientDetail } from '@wodi/shared';
import { Button, LoadingBlock, Notice, RadioGroup, Section, TextArea, useShortcut } from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { patientConsentsQuery, recordConsent } from '../api/patients.ts';
import { consentTextsQuery } from '../api/registration-config.ts';
import { useOnline } from '../hooks/use-online.ts';
import { formatInstant } from './format.ts';
import styles from './patients.module.css';
import { REASON_MIN_LENGTH } from './reason-dialog.tsx';

// Data-sharing consent on the patient record (registration plan §2.7, §6; workflow
// registration.md scenario 6): the current decision, recording a new one, withdrawing
// with a reason, and every earlier decision. Nothing is edited: each change is a new row.
// The wording read to the patient is the tenant's own (Settings > Registration); Wodi
// ships none (TODO(business): the approved wording, plan §12 decision 3).

export const DECISION_TEXT = {
  granted: 'Agreed to share',
  refused: 'Refused',
  withdrawn: 'Withdrawn',
} as const;

const LANGUAGE_LABELS = { en: 'English', sw: 'Kiswahili' } as const;

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function describe(consent: NonNullable<PatientDetail['consent']>): string {
  return `${DECISION_TEXT[consent.decision]}, verbally, on ${formatInstant(consent.capturedAt)} at ${consent.facility.name}. Recorded by ${consent.capturedBy.displayName}.`;
}

type Mode = 'record' | 'withdraw' | null;

export function ConsentSection({
  patient,
  canUpdate,
  canReadWording,
}: {
  patient: PatientDetail;
  canUpdate: boolean;
  /** The consent wording is read with patients.register; without it, consent can't be recorded here. */
  canReadWording: boolean;
}) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const texts = useQuery({ ...consentTextsQuery, enabled: canUpdate && canReadWording });
  const history = useQuery(patientConsentsQuery(patient.id));
  const [mode, setMode] = useState<Mode>(null);
  const [decision, setDecision] = useState<'granted' | 'refused' | ''>('');
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const active = (texts.data ?? []).filter((t) => t.status === 'active');
  const [textId, setTextId] = useState<string | null>(null);
  // The wording read, chosen by language when there is more than one; English first.
  const text =
    active.find((t) => t.id === textId) ?? active.find((t) => t.language === 'en') ?? active[0];
  const consent = patient.consent;
  const granted = consent?.decision === 'granted';

  const close = () => {
    setMode(null);
    setDecision('');
    setReason('');
    setTried(false);
  };
  const record = useMutation({
    mutationFn: () => {
      const trimmed = reason.trim();
      return recordConsent(patient.id, {
        purpose: 'hie_data_sharing',
        decision: mode === 'withdraw' ? 'withdrawn' : (decision as 'granted' | 'refused'),
        method: 'verbal',
        consentTextId: text?.id ?? '',
        reason: decision === 'granted' || trimmed === '' ? null : trimmed,
      });
    },
    onSuccess: async () => {
      close();
      await queryClient.invalidateQueries({ queryKey: ['patients', patient.id] });
    },
  });

  const reasonShort = reason.trim().length < REASON_MIN_LENGTH;
  const reasonNeeded = mode === 'withdraw' || (decision === 'refused' && reason.trim() !== '');
  const submit = () => {
    setTried(true);
    if (mode === 'record' && decision === '') return;
    if (reasonNeeded && reasonShort) return;
    record.mutate();
  };
  useShortcut('mod+enter', submit, {
    allowInFields: true,
    enabled: mode !== null && !record.isPending,
    description: mode === 'withdraw' ? 'Withdraw consent' : 'Save decision',
  });

  const canAct = canUpdate && mode === null && text !== undefined;
  const earlier = (history.data?.items ?? []).slice(1);
  return (
    <Section
      title="Data-sharing consent"
      {...(canAct
        ? {
            actions: (
              <Button
                dense
                isDisabled={!online}
                onPress={() => {
                  setMode(granted ? 'withdraw' : 'record');
                }}
              >
                {granted ? 'Withdraw consent' : 'Record consent'}
              </Button>
            ),
          }
        : {})}
    >
      {consent === null ? <p>Not asked yet.</p> : <p>{describe(consent)}</p>}
      {consent?.reason == null ? null : (
        <p className={styles.meta}>{`Reason given: ${consent.reason}`}</p>
      )}
      {canUpdate && !online && mode === null ? (
        <p className={styles.meta}>You're offline. Consent can be recorded once you reconnect.</p>
      ) : null}
      {canUpdate && !canReadWording ? (
        <p className={styles.meta}>
          Recording consent needs permission to register patients, to read the approved wording.
        </p>
      ) : null}
      {canUpdate && canReadWording && texts.isSuccess && text === undefined ? (
        <p className={styles.meta}>
          The consent wording isn't set up yet, so consent can't be recorded. An administrator adds
          it in Settings, Registration.
        </p>
      ) : null}
      {canUpdate && canReadWording && texts.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void texts.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(texts.error, "The consent wording couldn't be loaded. Try again.")}
        </Notice>
      ) : null}

      {mode !== null && text !== undefined ? (
        <form
          className={styles.inlineForm}
          aria-label={mode === 'withdraw' ? 'Withdraw consent' : 'Record consent'}
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          {mode === 'record' ? (
            <>
              {active.length > 1 ? (
                <RadioGroup
                  label="Language read to the patient"
                  orientation="horizontal"
                  value={text.id}
                  onChange={setTextId}
                  options={active.map((t) => ({ id: t.id, label: LANGUAGE_LABELS[t.language] }))}
                />
              ) : null}
              <p className={styles.consentText}>{text.body}</p>
              <RadioGroup
                label="The patient's decision"
                value={decision}
                onChange={(v) => {
                  setDecision(v as typeof decision);
                }}
                options={[
                  { id: 'granted', label: 'Agreed to share' },
                  { id: 'refused', label: 'Refused' },
                ]}
                {...(tried && decision === ''
                  ? { errorMessage: "Choose the patient's decision." }
                  : {})}
              />
              {decision === 'refused' ? (
                <TextArea
                  label="Reason (optional)"
                  description="In the patient's words, if they gave one. Kept with the decision."
                  value={reason}
                  onChange={setReason}
                  {...(tried && reasonNeeded && reasonShort
                    ? {
                        errorMessage: `Give at least ${String(REASON_MIN_LENGTH)} characters, or leave it empty.`,
                      }
                    : {})}
                />
              ) : null}
            </>
          ) : (
            <>
              <p>
                From now on Wodi sends nothing about this patient to the national health information
                exchange, including SHA checks and claims, until they agree again.
              </p>
              <TextArea
                label="Reason"
                description="Why the patient withdrew, in their words. Kept with the decision and audited."
                value={reason}
                onChange={setReason}
                autoFocus
                {...(tried && reasonShort
                  ? {
                      errorMessage: `Give a reason of at least ${String(REASON_MIN_LENGTH)} characters.`,
                    }
                  : {})}
              />
            </>
          )}
          {record.error === null ? null : (
            <Notice tone="error">
              {errorText(record.error, 'Consent was not saved. Try again.')}
            </Notice>
          )}
          <div className={styles.actions}>
            <Button
              type="submit"
              variant="primary"
              isDisabled={!online}
              isPending={record.isPending}
              pendingLabel="Saving…"
              shortcut="mod+enter"
            >
              {mode === 'withdraw' ? 'Withdraw consent' : 'Save decision'}
            </Button>
            <Button variant="quiet" onPress={close}>
              Cancel
            </Button>
          </div>
        </form>
      ) : null}

      {history.isPending ? <LoadingBlock label="Loading earlier decisions" rows={1} /> : null}
      {history.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void history.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(history.error, "Earlier decisions couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {earlier.length > 0 ? (
        <div>
          <h3 className={styles.meta}>Earlier decisions</h3>
          <ol className={styles.history} aria-label="Earlier consent decisions">
            {earlier.map((c) => (
              <li key={c.id} className={styles.historyItem}>
                <span className={styles.meta}>
                  {`${formatInstant(c.capturedAt)}, ${c.capturedBy.displayName}`}
                </span>
                <span>
                  {`${DECISION_TEXT[c.decision]}${c.reason === null ? '' : `: ${c.reason}`}`}
                </span>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </Section>
  );
}
